import React from "react";
import { Link } from "react-router-dom";
import LegalShell, { Section } from "@/components/LegalShell";
import { Button } from "@/components/ui/button";
import { ShieldCheck, IdentificationCard, Lock, Eye, HandHeart, Warning } from "@phosphor-icons/react";

export default function SafeMatch() {
  return (
    <div data-testid="safe-match-page">
      <LegalShell eyebrow="Trust" title="Safe Match Pledge" updated="7 February 2026">
        <Section title="Six commitments to every family who joins us">
          <p>
            Nikah is not a game. It is a covenant. This pledge lists the
            six protections we make binding on ourselves — and how we will
            handle it if we ever fall short.
          </p>
        </Section>

        <div className="mt-6 grid md:grid-cols-2 gap-px bg-brand-line border border-brand-line">
          {[
            { icon: IdentificationCard, title: "Verified identity", body: "Every profile is checked against government-issued ID before it is visible. No exceptions." },
            { icon: ShieldCheck, title: "Family reference check", body: "New members provide a family reference (parent, sibling, or wali) that we contact discreetly." },
            { icon: Lock, title: "Contact-info gate", body: "Your email and phone are hidden until both families explicitly accept — or a paid member unlocks it." },
            { icon: Eye, title: "Human review", body: "Every match, interest, and story on the platform is monitored by a human matchmaker — not just an algorithm." },
            { icon: HandHeart, title: "Adab-first messaging", body: "Any message flagged as coercive, indecent, or transactional triggers an immediate review and, if confirmed, permanent removal." },
            { icon: Warning, title: "One-tap reporting", body: "Reporting any profile takes ten seconds. Investigations begin within one working day, in-shaa-Allah." },
          ].map((p) => (
            <div key={p.title} className="bg-white p-8">
              <p.icon size={26} weight="duotone" className="text-brand-terracotta" />
              <h3 className="font-serif text-2xl mt-4 text-brand-ink">{p.title}</h3>
              <p className="mt-2 text-brand-muted leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>

        <Section title="If something goes wrong">
          <p>
            If any of the six commitments above is ever broken in your
            experience — even in the smallest way — please write to us
            immediately at <a className="text-brand-terracotta underline underline-offset-4" href="mailto:rashidhussain896@gmail.com">rashidhussain896@gmail.com</a> or
            submit the report form below. You will hear back from a real
            person, and if the matter is serious we will suspend the concerned
            profile within hours.
          </p>
        </Section>

        <Section title="Zero-tolerance list">
          <p>The following behaviours cause an immediate ban with no appeal:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Requesting money, gifts, or investments from other members.</li>
            <li>Sending explicit content or coercive messages.</li>
            <li>Creating multiple profiles or impersonating another person.</li>
            <li>Solicitation of any commercial service via the platform.</li>
          </ul>
        </Section>

        <div className="mt-12 border-t border-brand-line pt-8 flex flex-wrap items-center gap-4">
          <Button asChild className="rounded-full bg-brand-terracotta hover:bg-brand-terracotta/90 text-white px-8 py-6" data-testid="safe-cta-report">
            <Link to="/report">Report an Issue →</Link>
          </Button>
          <Button asChild variant="outline" className="rounded-full border-brand-line px-8 py-6">
            <Link to="/contact">Speak with us</Link>
          </Button>
        </div>
      </LegalShell>
    </div>
  );
}
